/**
 * Virtualized message list for chat channels.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/joy';
import { useVirtualizer } from '@tanstack/react-virtual';
import MessageItem from './MessageItem';
import type { Message } from '../../types/chat';

interface MessageListProps {
  messages: Message[];
  channelId?: string | null;
  isLoading?: boolean;
  isFetchingMore?: boolean;
  hasMoreMessages?: boolean;
  onLoadMore?: () => void;
  currentUserId?: string;
  onReaction?: (messageId: string, emoji: string) => void;
  onRemoveReaction?: (messageId: string, emoji: string) => void;
  onEdit?: (messageId: string, content: string) => void;
  onDelete?: (messageId: string) => void;
  onReply?: (messageId: string) => void;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  channelId,
  isLoading = false,
  isFetchingMore = false,
  hasMoreMessages = false,
  onLoadMore,
  currentUserId,
  onReaction,
  onRemoveReaction,
  onEdit,
  onDelete,
  onReply,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const scrollToBottomRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevMessagesLengthRef = useRef(messages.length);
  const prevScrollHeightRef = useRef(0);

  // Virtualizer for efficient rendering
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80, // Estimated message height
    overscan: 5,
  });

  // Check if user is near bottom
  const checkIfNearBottom = useCallback(() => {
    const element = parentRef.current;
    if (!element) return true;
    const threshold = 100;
    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom < threshold;
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    scrollToBottomRef.current?.scrollIntoView({ behavior });
  }, []);

  // Scroll to bottom on new messages if user was near bottom
  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current && isNearBottomRef.current) {
      // Small delay to allow render
      setTimeout(() => scrollToBottom(), 50);
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Scroll to bottom when channel changes or messages first load
  useEffect(() => {
    if (messages.length > 0 && !isLoading) {
      // Small delay to ensure DOM is updated
      setTimeout(() => scrollToBottom('auto'), 100);
    }
  }, [channelId, scrollToBottom, isLoading]);

  // Track scroll position and load more when near top
  const handleScroll = useCallback(() => {
    isNearBottomRef.current = checkIfNearBottom();

    // Load more messages when scrolling near the top
    const element = parentRef.current;
    if (element && hasMoreMessages && !isFetchingMore && onLoadMore) {
      const threshold = 100;
      if (element.scrollTop < threshold) {
        // Save scroll height before loading more
        prevScrollHeightRef.current = element.scrollHeight;
        onLoadMore();
      }
    }
  }, [checkIfNearBottom, hasMoreMessages, isFetchingMore, onLoadMore]);

  // Maintain scroll position when loading older messages
  useEffect(() => {
    const element = parentRef.current;
    if (element && prevScrollHeightRef.current > 0) {
      const newScrollHeight = element.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
      if (scrollDiff > 0) {
        element.scrollTop = scrollDiff;
      }
      prevScrollHeightRef.current = 0;
    }
  }, [messages]);

  // Group messages by date
  const getMessageDate = (timestamp: string): string => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });
    }
  };

  // Determine if we should show date separator
  const shouldShowDateSeparator = (index: number): boolean => {
    if (index === 0) return true;
    const currentDate = getMessageDate(messages[index].created_at);
    const prevDate = getMessageDate(messages[index - 1].created_at);
    return currentDate !== prevDate;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size="md" />
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Typography level="body-md" color="neutral" textAlign="center">
          No messages yet. Start the conversation!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={parentRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowX: 'hidden',
        overflowY: 'auto',
        p: { xs: 1, md: 2 },
      }}
    >
      {/* Loading indicator for older messages */}
      {isFetchingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      )}

      <Box
        sx={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const message = messages[virtualItem.index];
          const showDateSeparator = shouldShowDateSeparator(virtualItem.index);

          return (
            <Box
              key={virtualItem.key}
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {showDateSeparator && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 2,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 'xl',
                      bgcolor: 'background.level1',
                    }}
                  >
                    <Typography level="body-xs" color="neutral">
                      {getMessageDate(message.created_at)}
                    </Typography>
                  </Box>
                </Box>
              )}
              <MessageItem
                message={message}
                isOwnMessage={message.sender?.id === currentUserId}
                onReaction={onReaction}
                onRemoveReaction={onRemoveReaction}
                onEdit={onEdit}
                onDelete={onDelete}
                onReply={onReply}
              />
            </Box>
          );
        })}
      </Box>

      {/* Scroll anchor */}
      <div ref={scrollToBottomRef} />
    </Box>
  );
};

export default MessageList;
